import type {Dayjs} from 'dayjs';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import {d} from '@/utils/consoleHelper.ts';
import type {DateTimeString} from '@/types/order.ts';
import {pluralize} from '@/utils/text';

dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * Format a date with a default format or supplied.
 *
 * @param date - The date to be formatted, can be a Date object, a string, or a number.
 * @param format - The format in which to display the date.
 *
 * @return The formatted date string.
 */
export function formatTripDate(date: Date | string | number, format: string = 'MMM DD, YYYY @ h:mma'): string {
    return dayjs(date).format(format);
}

export const fmtDateString = (d: dayjs.Dayjs): DateTimeString => d.format('YYYY-MM-DD HH:mm:00') as DateTimeString;

/**
 * Human-readable rental duration for order date pickers.
 */
export function formatOrderDurationDisplay(
    startsAt: string | null | undefined,
    stopsAt: string | null | undefined,
): string {
    if (!startsAt || !stopsAt) {
        return '';
    }

    const start = dayjs(startsAt);
    const end = dayjs(stopsAt);

    if (!start.isValid() || !end.isValid()) {
        return '';
    }

    if (end.isBefore(start)) {
        return 'Invalid duration';
    }

    if (end.isSame(start)) {
        return 'No duration';
    }

    const days = end.diff(start, 'day');
    const hours = end.diff(start, 'hour') % 24;

    if (days === 0 && hours === 0) {
        return 'No duration';
    }

    const parts: string[] = [];

    if (days > 0) {
        parts.push(`${days} ${pluralize(days, 'day')}`);
    }

    if (hours > 0) {
        parts.push(`${hours} ${pluralize(hours, 'hour')}`);
    }

    parts.push('Trip');

    return parts.join(' ');
}

export const guessTimeZone = dayjs.tz?.guess();

export const getUserTimezone = () => {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

export const calcMiles = (
    dates: { start: Date | Dayjs, end: Date | Dayjs },
    milesIncluded: number,
    maxDistance?: number
): string => {
    const startDate = dates?.start ? dayjs(dates.start) : dayjs();
    const endDate = dates?.end ? dayjs(dates.end) : dayjs();

    if (milesIncluded === -1) return 'unlimited';

    const units = endDate.diff(startDate, 'days') + (endDate.diff(startDate, 'hours') % 24 > 0 ? 1 : 0);
    const allowance = units * milesIncluded;


    const x = maxDistance ? Math.min(maxDistance, allowance) : allowance;

    if (x === 0) return '0';

    return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

export type TimeSlot = { title: string; value: string };

/** Wall-clock time on `day` from a possibly fractional hour (9.5 => 09:30). */
function atHour(day: Dayjs, hour: number): Dayjs {
    const wholeHours = Math.floor(hour);
    const minutes = Math.round((hour - wholeHours) * 60);

    return day.hour(wholeHours).minute(minutes).second(0).millisecond(0);
}

export function generateReservationTimes(
    startHour: number = 0,          // Opening hour in a 24-hour format (fractional hours allowed: 9.5 = 09:30)
    endHour: number = 24,           // Closing hour in a 24-hour format (fractional hours allowed)
    interval: number = 15,          // Interval between time slots in minutes
    selectedDate: Dayjs | Date | string | null = null, // The selected date (e.g., `currentSelectedDates.value.start`)
    allowPastTime: boolean = false, // Whether past times are allowed for today
    inclusiveEnd: boolean = false   // Whether a slot exactly at endHour is offered (operating hours close inclusively)
): TimeSlot[] {
    const times: { title: string; value: string }[] = [];
    const baseDay = dayjs();
    const now = baseDay;
    const start = atHour(baseDay, startHour);
    const end = atHour(baseDay, endHour);

    let effectiveStart = start;

    // Check if the selected date is today
    const isToday = selectedDate ? dayjs().isSame(selectedDate, 'day') : false;

    // Adjust the start time if it's today and past times are not allowed
    if (isToday && !allowPastTime && now.isAfter(start)) {
        const minutesSinceStart = now.diff(start, 'minutes');
        const nextIntervalMinutes = Math.ceil(minutesSinceStart / interval) * interval;
        effectiveStart = start.add(nextIntervalMinutes, 'minute');
    }

    // Validate if the time range is valid
    if (startHour >= endHour || interval <= 0) {
        d.warn('Invalid time range or interval');
        return times; // Return an empty array if invalid
    }

    // Generate time slots between the effective start and end
    for (let time = effectiveStart; time.isBefore(end) || (inclusiveEnd && time.isSame(end)); time = time.add(interval, 'minute')) {
        const timeValue = time.format('HH:mm:00');
        const timeTitle = timeValue === '12:00:00' ? 'Noon' : time.format('hh:mm a');

        times.push({
            title: timeTitle,
            value: timeValue,
        });
    }

    return times;
}

export const timezones = [
    'Europe/Andorra',
    'Asia/Dubai',
    'Asia/Kabul',
    'Europe/Tirane',
    'Asia/Yerevan',
    'Antarctica/Casey',
    'Antarctica/Davis',
    'Antarctica/DumontDUrville', // https://bugs.chromium.org/p/chromium/issues/detail?id=928068
    'Antarctica/Mawson',
    'Antarctica/Palmer',
    'Antarctica/Rothera',
    'Antarctica/Syowa',
    'Antarctica/Troll',
    'Antarctica/Vostok',
    'America/Argentina/Buenos_Aires',
    'America/Argentina/Cordoba',
    'America/Argentina/Salta',
    'America/Argentina/Jujuy',
    'America/Argentina/Tucuman',
    'America/Argentina/Catamarca',
    'America/Argentina/La_Rioja',
    'America/Argentina/San_Juan',
    'America/Argentina/Mendoza',
    'America/Argentina/San_Luis',
    'America/Argentina/Rio_Gallegos',
    'America/Argentina/Ushuaia',
    'Pacific/Pago_Pago',
    'Europe/Vienna',
    'Australia/Lord_Howe',
    'Antarctica/Macquarie',
    'Australia/Hobart',
    'Australia/Currie',
    'Australia/Melbourne',
    'Australia/Sydney',
    'Australia/Broken_Hill',
    'Australia/Brisbane',
    'Australia/Lindeman',
    'Australia/Adelaide',
    'Australia/Darwin',
    'Australia/Perth',
    'Australia/Eucla',
    'Asia/Baku',
    'America/Barbados',
    'Asia/Dhaka',
    'Europe/Brussels',
    'Europe/Sofia',
    'Atlantic/Bermuda',
    'Asia/Brunei',
    'America/La_Paz',
    'America/Noronha',
    'America/Belem',
    'America/Fortaleza',
    'America/Recife',
    'America/Araguaina',
    'America/Maceio',
    'America/Bahia',
    'America/Sao_Paulo',
    'America/Campo_Grande',
    'America/Cuiaba',
    'America/Santarem',
    'America/Porto_Velho',
    'America/Boa_Vista',
    'America/Manaus',
    'America/Eirunepe',
    'America/Rio_Branco',
    'America/Nassau',
    'Asia/Thimphu',
    'Europe/Minsk',
    'America/Belize',
    'America/St_Johns',
    'America/Halifax',
    'America/Glace_Bay',
    'America/Moncton',
    'America/Goose_Bay',
    'America/Blanc-Sablon',
    'America/Toronto',
    'America/Nipigon',
    'America/Thunder_Bay',
    'America/Iqaluit',
    'America/Pangnirtung',
    'America/Atikokan',
    'America/Winnipeg',
    'America/Rainy_River',
    'America/Resolute',
    'America/Rankin_Inlet',
    'America/Regina',
    'America/Swift_Current',
    'America/Edmonton',
    'America/Cambridge_Bay',
    'America/Yellowknife',
    'America/Inuvik',
    'America/Creston',
    'America/Dawson_Creek',
    'America/Fort_Nelson',
    'America/Vancouver',
    'America/Whitehorse',
    'America/Dawson',
    'Indian/Cocos',
    'Europe/Zurich',
    'Africa/Abidjan',
    'Pacific/Rarotonga',
    'America/Santiago',
    'America/Punta_Arenas',
    'Pacific/Easter',
    'Asia/Shanghai',
    'Asia/Urumqi',
    'America/Bogota',
    'America/Costa_Rica',
    'America/Havana',
    'Atlantic/Cape_Verde',
    'America/Curacao',
    'Indian/Christmas',
    'Asia/Nicosia',
    'Asia/Famagusta',
    'Europe/Prague',
    'Europe/Berlin',
    'Europe/Copenhagen',
    'America/Santo_Domingo',
    'Africa/Algiers',
    'America/Guayaquil',
    'Pacific/Galapagos',
    'Europe/Tallinn',
    'Africa/Cairo',
    'Africa/El_Aaiun',
    'Europe/Madrid',
    'Africa/Ceuta',
    'Atlantic/Canary',
    'Europe/Helsinki',
    'Pacific/Fiji',
    'Atlantic/Stanley',
    'Pacific/Chuuk',
    'Pacific/Pohnpei',
    'Pacific/Kosrae',
    'Atlantic/Faroe',
    'Europe/Paris',
    'Europe/London',
    'Asia/Tbilisi',
    'America/Cayenne',
    'Africa/Accra',
    'Europe/Gibraltar',
    'America/Godthab',
    'America/Danmarkshavn',
    'America/Scoresbysund',
    'America/Thule',
    'Europe/Athens',
    'Atlantic/South_Georgia',
    'America/Guatemala',
    'Pacific/Guam',
    'Africa/Bissau',
    'America/Guyana',
    'Asia/Hong_Kong',
    'America/Tegucigalpa',
    'America/Port-au-Prince',
    'Europe/Budapest',
    'Asia/Jakarta',
    'Asia/Pontianak',
    'Asia/Makassar',
    'Asia/Jayapura',
    'Europe/Dublin',
    'Asia/Jerusalem',
    'Asia/Kolkata',
    'Indian/Chagos',
    'Asia/Baghdad',
    'Asia/Tehran',
    'Atlantic/Reykjavik',
    'Europe/Rome',
    'America/Jamaica',
    'Asia/Amman',
    'Asia/Tokyo',
    'Africa/Nairobi',
    'Asia/Bishkek',
    'Pacific/Tarawa',
    'Pacific/Enderbury',
    'Pacific/Kiritimati',
    'Asia/Pyongyang',
    'Asia/Seoul',
    'Asia/Almaty',
    'Asia/Qyzylorda',
    'Asia/Qostanay', // https://bugs.chromium.org/p/chromium/issues/detail?id=928068
    'Asia/Aqtobe',
    'Asia/Aqtau',
    'Asia/Atyrau',
    'Asia/Oral',
    'Asia/Beirut',
    'Asia/Colombo',
    'Africa/Monrovia',
    'Europe/Vilnius',
    'Europe/Luxembourg',
    'Europe/Riga',
    'Africa/Tripoli',
    'Africa/Casablanca',
    'Europe/Monaco',
    'Europe/Chisinau',
    'Pacific/Majuro',
    'Pacific/Kwajalein',
    'Asia/Yangon',
    'Asia/Ulaanbaatar',
    'Asia/Hovd',
    'Asia/Choibalsan',
    'Asia/Macau',
    'America/Martinique',
    'Europe/Malta',
    'Indian/Mauritius',
    'Indian/Maldives',
    'America/Mexico_City',
    'America/Cancun',
    'America/Merida',
    'America/Monterrey',
    'America/Matamoros',
    'America/Mazatlan',
    'America/Chihuahua',
    'America/Ojinaga',
    'America/Hermosillo',
    'America/Tijuana',
    'America/Bahia_Banderas',
    'Asia/Kuala_Lumpur',
    'Asia/Kuching',
    'Africa/Maputo',
    'Africa/Windhoek',
    'Pacific/Noumea',
    'Pacific/Norfolk',
    'Africa/Lagos',
    'America/Managua',
    'Europe/Amsterdam',
    'Europe/Oslo',
    'Asia/Kathmandu',
    'Pacific/Nauru',
    'Pacific/Niue',
    'Pacific/Auckland',
    'Pacific/Chatham',
    'America/Panama',
    'America/Lima',
    'Pacific/Tahiti',
    'Pacific/Marquesas',
    'Pacific/Gambier',
    'Pacific/Port_Moresby',
    'Pacific/Bougainville',
    'Asia/Manila',
    'Asia/Karachi',
    'Europe/Warsaw',
    'America/Miquelon',
    'Pacific/Pitcairn',
    'America/Puerto_Rico',
    'Asia/Gaza',
    'Asia/Hebron',
    'Europe/Lisbon',
    'Atlantic/Madeira',
    'Atlantic/Azores',
    'Pacific/Palau',
    'America/Asuncion',
    'Asia/Qatar',
    'Indian/Reunion',
    'Europe/Bucharest',
    'Europe/Belgrade',
    'Europe/Kaliningrad',
    'Europe/Moscow',
    'Europe/Simferopol',
    'Europe/Kirov',
    'Europe/Astrakhan',
    'Europe/Volgograd',
    'Europe/Saratov',
    'Europe/Ulyanovsk',
    'Europe/Samara',
    'Asia/Yekaterinburg',
    'Asia/Omsk',
    'Asia/Novosibirsk',
    'Asia/Barnaul',
    'Asia/Tomsk',
    'Asia/Novokuznetsk',
    'Asia/Krasnoyarsk',
    'Asia/Irkutsk',
    'Asia/Chita',
    'Asia/Yakutsk',
    'Asia/Khandyga',
    'Asia/Vladivostok',
    'Asia/Ust-Nera',
    'Asia/Magadan',
    'Asia/Sakhalin',
    'Asia/Srednekolymsk',
    'Asia/Kamchatka',
    'Asia/Anadyr',
    'Asia/Riyadh',
    'Pacific/Guadalcanal',
    'Indian/Mahe',
    'Africa/Khartoum',
    'Europe/Stockholm',
    'Asia/Singapore',
    'America/Paramaribo',
    'Africa/Juba',
    'Africa/Sao_Tome',
    'America/El_Salvador',
    'Asia/Damascus',
    'America/Grand_Turk',
    'Africa/Ndjamena',
    'Indian/Kerguelen',
    'Asia/Bangkok',
    'Asia/Dushanbe',
    'Pacific/Fakaofo',
    'Asia/Dili',
    'Asia/Ashgabat',
    'Africa/Tunis',
    'Pacific/Tongatapu',
    'Europe/Istanbul',
    'America/Port_of_Spain',
    'Pacific/Funafuti',
    'Asia/Taipei',
    'Europe/Kiev',
    'Europe/Uzhgorod',
    'Europe/Zaporozhye',
    'Pacific/Wake',
    'America/New_York',
    'America/Detroit',
    'America/Kentucky/Louisville',
    'America/Kentucky/Monticello',
    'America/Indiana/Indianapolis',
    'America/Indiana/Vincennes',
    'America/Indiana/Winamac',
    'America/Indiana/Marengo',
    'America/Indiana/Petersburg',
    'America/Indiana/Vevay',
    'America/Chicago',
    'America/Indiana/Tell_City',
    'America/Indiana/Knox',
    'America/Menominee',
    'America/North_Dakota/Center',
    'America/North_Dakota/New_Salem',
    'America/North_Dakota/Beulah',
    'America/Denver',
    'America/Boise',
    'America/Phoenix',
    'America/Los_Angeles',
    'America/Anchorage',
    'America/Juneau',
    'America/Sitka',
    'America/Metlakatla',
    'America/Yakutat',
    'America/Nome',
    'America/Adak',
    'Pacific/Honolulu',
    'America/Montevideo',
    'Asia/Samarkand',
    'Asia/Tashkent',
    'America/Caracas',
    'Asia/Ho_Chi_Minh',
    'Pacific/Efate',
    'Pacific/Wallis',
    'Pacific/Apia',
    'Africa/Johannesburg',
];

/**
 * Time options for one end of a trip. Keeps the current time selectable even
 * when it is off the interval grid, and on a same-day trip hides pickup times
 * at or after the return (and return times at or before the pickup).
 */
export function tripTimeOptions(
    slots: TimeSlot[],
    current: string | null,
    bound: { time: string; keep: 'before' | 'after' } | null = null,
): TimeSlot[] {
    const options = [...slots];

    if (current && !options.some((slot) => slot.value === current)) {
        options.push({title: dayjs(`2000-01-01 ${current}`).format('hh:mm a'), value: current});
        options.sort((a, b) => a.value.localeCompare(b.value));
    }

    if (!bound) {
        return options;
    }

    return options.filter((slot) => bound.keep === 'before' ? slot.value < bound.time : slot.value > bound.time);
}

/**
 * Compact trip range for lists: "Jul 2 – 3, 2026", "Jul 30 – Aug 2, 2026",
 * or "Dec 30, 2025 – Jan 2, 2026" when the years differ.
 */
export function formatTripRange(start?: string | null, end?: string | null): string {
    const from = start ? dayjs(start) : null;
    const to = end ? dayjs(end) : null;

    if (!from?.isValid()) return to?.isValid() ? to.format('MMM D, YYYY') : '';
    if (!to?.isValid()) return from.format('MMM D, YYYY');

    if (!from.isSame(to, 'year')) return `${from.format('MMM D, YYYY')} – ${to.format('MMM D, YYYY')}`;
    if (from.isSame(to, 'day')) return from.format('MMM D, YYYY');
    if (from.isSame(to, 'month')) return `${from.format('MMM D')} – ${to.format('D, YYYY')}`;

    return `${from.format('MMM D')} – ${to.format('MMM D, YYYY')}`;
}
